import {
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import { getMarketsWithAttributes } from "../markets/marketService";
import { getMarketPriceDriverCombinations } from "../markets/marketPriceDriverCombinationService";
import { getSpotByTermSchedule } from "../markets/termService";
import { getVenues } from "../markets/venueService";
import { getOrAddInstrument } from "../marketdata/instrumentService";
import {
  getFilteredProductForMarket,
} from "../products/productUtilities";
import { formatDate, normalizeExpiryDateTime } from "../utilities";

export const TRADING_HUB_ROLES = [
  "administrator",
  "operator",
  "trader",
  "trading-company-administrator",
];

export function hasTradingHubAccess(keycloak) {
  const roles = keycloak?.tokenParsed?.role || [];
  return TRADING_HUB_ROLES.some((role) => roles.includes(role));
}

class TradingService {
  constructor() {
    this.connection = null;
    this.connectingPromise = null;
    this.messageHub = null;
    this.keycloak = null;
    this.setters = {};
    this.lifecycleHandlersBound = false;
    this.seedMarketInstruments = [];
    this.engineMarketInstruments = [];
    this.seedRequestToken = 0;
    this.cache = {
      marketControl: [],
      marketsOpen: null,
      marketInstruments: [],
      marketOrders: [],
      marketTrades: [],
      organizationOrders: [],
      organizationTrades: [],
    };
  }

  initialize({ keycloak, messageHub, setters }) {
    this.keycloak = keycloak;
    this.messageHub = messageHub;
    this.setters = setters;

    Object.entries(this.cache).forEach(([key, value]) => {
      const setter = setters[`set${capitalize(key)}`];
      if (setter) {
        setter(value);
      }
    });

    this.bindLifecycleHandlers();

    if (this.connection) {
      this.registerHandlers(this.connection);
    }
  }

  shouldPauseReconnect() {
    const isHidden =
      typeof document !== "undefined" && document.visibilityState === "hidden";
    const isOffline =
      typeof navigator !== "undefined" && navigator.onLine === false;

    return isHidden || isOffline;
  }

  getReconnectDelay(previousRetryCount = 0) {
    const delays = [0, 2000, 10000, 30000];
    return delays[Math.min(previousRetryCount, delays.length - 1)];
  }

  bindLifecycleHandlers() {
    if (
      this.lifecycleHandlersBound ||
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    const resume = () => {
      void this.resumeConnectionIfNeeded();
    };

    window.addEventListener("online", resume);
    window.addEventListener("focus", resume);
    window.addEventListener("pageshow", resume);
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") {
        resume();
      }
    });

    this.lifecycleHandlersBound = true;
  }

  async resumeConnectionIfNeeded() {
    if (!this.keycloak || !this.messageHub || !hasTradingHubAccess(this.keycloak)) {
      return;
    }

    if (!this.connection) {
      await this.connect();
      return;
    }

    if (this.connection.state === HubConnectionState.Disconnected) {
      await this.connect();
    }
  }

  createConnection() {
    const connection = new HubConnectionBuilder()
      .withUrl(this.messageHub, {
        accessTokenFactory: () => this.keycloak.token,
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (retryContext) => {
          if (this.shouldPauseReconnect()) {
            return null;
          }

          return this.getReconnectDelay(retryContext.previousRetryCount);
        },
      })
      .configureLogging(LogLevel.Information)
      .build();

    connection.onclose(() => {
      if (!this.shouldPauseReconnect()) {
        window.setTimeout(() => {
          void this.resumeConnectionIfNeeded();
        }, this.getReconnectDelay(3));
      }
    });

    return connection;
  }

  deriveMarketsOpenFromMarketControl(marketControl = []) {
    const activeMarkets = (marketControl || []).filter((market) => market.active);

    if (!activeMarkets.length) {
      return null;
    }

    return activeMarkets.some((market) => market.halted !== true);
  }

  publishMarketsOpenStatus(marketsOpen) {
    this.cache.marketsOpen = marketsOpen;
    this.setters.setMarketsOpen && this.setters.setMarketsOpen(marketsOpen);
  }

  startConnection(connection) {
    this.registerHandlers(connection);

    this.connectingPromise = connection
      .start()
      .then(() => {
        this.connection = connection;
        console.log("TradingService connected");
        return connection;
      })
      .catch((error) => {
        console.error("TradingService connection failed:", error);
        if (!this.shouldPauseReconnect()) {
          toast.error("Failed to connect to trading engine");
        }
        throw error;
      })
      .finally(() => {
        this.connectingPromise = null;
      });

    return this.connectingPromise;
  }

  async connect() {
    if (this.connectingPromise) return this.connectingPromise;
    if (!this.messageHub || !this.keycloak) {
      console.error("TradingService not initialized yet.");
      return null;
    }
    if (!hasTradingHubAccess(this.keycloak)) {
      console.info(
        "TradingService connection skipped for user without trading hub role.",
      );
      return null;
    }

    if (!this.connection) {
      this.connection = this.createConnection();
      return this.startConnection(this.connection);
    }

    if (
      this.connection.state === HubConnectionState.Connected ||
      this.connection.state === HubConnectionState.Connecting ||
      this.connection.state === HubConnectionState.Reconnecting
    ) {
      return this.connection;
    }

    return this.startConnection(this.connection);
  }

  registerHandlers(connection) {
    [
      "MarketControl",
      "MarketInstruments",
      "MarketOrders",
      "MarketTrades",
      "OrganizationOrders",
      "OrganizationTrades",
      "UserAccountConnected",
      "UserAccountDisconnected",
      "Order",
      "Trade",
      "OrganizationOrder",
      "OrganizationTrade",
      "OrderConfirm",
      "TradeConfirmationUpdated",
      "MarketsStatusChanged",
      "InstrumentExpired",
      "InstrumentRemoved",
    ].forEach((eventName) => connection.off(eventName));

    connection.on("MarketControl", (data) => {
      this.cache.marketControl = data;
      this.setters.setMarketControl && this.setters.setMarketControl(data);
      this.publishMarketsOpenStatus(
        this.deriveMarketsOpenFromMarketControl(data),
      );
      void this.seedConfiguredMarketInstruments(data);
    });

    connection.on("MarketInstruments", (data) => {
      this.engineMarketInstruments = this.mergeInstruments([], data);
      this.publishMarketInstruments();
    });

    connection.on("MarketOrders", (data) => {
      this.cache.marketOrders = data;
      this.mergeMarketInstruments(data);
      this.setters.setMarketOrders && this.setters.setMarketOrders(data);
    });

    connection.on("MarketTrades", (data) => {
      this.cache.marketTrades = data;
      this.mergeMarketInstruments(data);
      this.setters.setMarketTrades && this.setters.setMarketTrades(data);
    });

    connection.on("OrganizationOrders", (data) => {
      this.cache.organizationOrders = data;
      this.setters.setOrganizationOrders &&
        this.setters.setOrganizationOrders(data);
    });

    connection.on("OrganizationTrades", (data) => {
      this.cache.organizationTrades = data;
      this.setters.setOrganizationTrades &&
        this.setters.setOrganizationTrades(data);
    });

    connection.on("UserAccountConnected", (user) => {
      toast.info(`User Connected: ${user.login}`);
    });

    connection.on("UserAccountDisconnected", (user) => {
      toast.info(`User Disconnected: ${user.login}`);
    });

    connection.on("Order", (order) => {
      this.mergeMarketInstruments([order]);
      this.setters.setMarketOrders &&
        this.setters.setMarketOrders((prev) => this.processOrder(prev, order));
    });

    connection.on("Trade", (trade) => {
      this.mergeMarketInstruments([trade]);
      this.setters.setMarketTrades &&
        this.setters.setMarketTrades((prev) => this.processTrade(prev, trade));
    });

    connection.on("OrganizationOrder", (order) => {
      this.setters.setOrganizationOrders &&
        this.setters.setOrganizationOrders((prev) =>
          this.processOrder(prev, order),
        );
    });

    connection.on("OrganizationTrade", (trade) => {
      this.setters.setOrganizationTrades &&
        this.setters.setOrganizationTrades((prev) =>
          this.processOrganizationTrade(prev, trade),
        );
    });

    connection.on("OrderConfirm", (orderConfirm) => {
      const { orderConfirmStatus, orderConfirmReason } = orderConfirm;
      if (orderConfirmStatus === "Accepted") {
        toast.success(`Order Accepted: ${orderConfirmReason}`);
      } else if (orderConfirmStatus === "Rejected") {
        toast.error(`Order Rejected: ${orderConfirmReason}`);
      } else {
        toast.warning(
          `Unknown Status: ${orderConfirmStatus} (${orderConfirmReason})`,
        );
      }
    });

    connection.on("TradeConfirmationUpdated", (notification) => {
      this.setters.setTradeConfirmationNotification &&
        this.setters.setTradeConfirmationNotification({
          ...notification,
          receivedAt: new Date().toISOString(),
        });
    });

    connection.on("MarketsStatusChanged", (notification) => {
      const marketsOpen =
        typeof notification?.marketsOpen === "boolean"
          ? notification.marketsOpen
          : typeof notification?.MarketsOpen === "boolean"
            ? notification.MarketsOpen
            : null;

      if (marketsOpen !== null) {
        this.publishMarketsOpenStatus(marketsOpen);
      }

      if (marketsOpen === true) {
        toast.success("Markets Opened");
      } else if (marketsOpen === false) {
        toast.error("Markets Closed");
      }
    });

    connection.on("InstrumentExpired", (notification) => {
      const instrumentId = this.getInstrumentId(notification);
      if (!instrumentId) {
        return;
      }

      const nextInstruments = this.engineMarketInstruments.map((instrument) =>
        String(instrument.instrumentId) === String(instrumentId)
          ? { ...instrument, isExpired: true }
          : instrument,
      );

      if (
        JSON.stringify(nextInstruments) !==
        JSON.stringify(this.engineMarketInstruments)
      ) {
        this.engineMarketInstruments = nextInstruments;
        this.publishMarketInstruments();
      }
    });

    connection.on("InstrumentRemoved", (notification) => {
      const instrumentId = this.getInstrumentId(notification);
      if (!instrumentId) {
        return;
      }

      const nextInstruments = this.engineMarketInstruments.filter(
        (instrument) => String(instrument.instrumentId) !== String(instrumentId),
      );

      if (nextInstruments.length !== this.engineMarketInstruments.length) {
        this.engineMarketInstruments = nextInstruments;
        this.publishMarketInstruments();
      }
    });
  }

  buildInstrumentSeedKey({
    marketId,
    termId,
    priceBasisId,
    currencyId,
    uomId,
    qualityAttributeValues = [],
  }) {
    const qualityAttributeValueIds = (qualityAttributeValues || [])
      .map((qualityAttributeValue) => qualityAttributeValue?.qualityAttributeValueId)
      .filter(Boolean)
      .map(String)
      .sort()
      .join("|");

    return [
      marketId,
      termId,
      priceBasisId,
      currencyId,
      uomId,
      qualityAttributeValueIds,
    ]
      .map((value) => String(value ?? ""))
      .join("::");
  }

  buildCoreCombinationRows(productState, combinations = []) {
    const allQualityAttributeValues =
      productState?.originalQualityAttributeValues ??
      productState?.filteredProduct?.qualityAttributeValues ??
      [];
    const qualityAttributeValuesById = new Map(
      allQualityAttributeValues.map((qualityAttributeValue) => [
        String(qualityAttributeValue?.qualityAttributeValueId),
        qualityAttributeValue,
      ]),
    );
    const rowsByKey = new Map();

    (combinations || [])
      .filter(
        (combination) => combination?.active !== false && combination?.isCore,
      )
      .forEach((combination) => {
        const qualityAttributeValueIds = (
          combination?.qualityAttributeValueIds || []
        )
          .filter(Boolean)
          .map(String);

        if (!qualityAttributeValueIds.length) {
          return;
        }

        const qualityAttributeValues = qualityAttributeValueIds
          .map((qualityAttributeValueId) =>
            qualityAttributeValuesById.get(qualityAttributeValueId),
          )
          .filter(Boolean);

        if (qualityAttributeValues.length !== qualityAttributeValueIds.length) {
          return;
        }

        const rowKey = qualityAttributeValueIds.sort().join("|");
        if (!rowsByKey.has(rowKey)) {
          rowsByKey.set(rowKey, {
            combinationId: combination.combinationId,
            qualityAttributeValues,
          });
        }
      });

    return [...rowsByKey.values()];
  }

  async seedConfiguredMarketInstruments(marketControl = []) {
    const activeMarketControls = (marketControl || []).filter(
      (candidate) => candidate?.active,
    );
    const requestToken = ++this.seedRequestToken;

    if (!activeMarketControls.length) {
      this.seedMarketInstruments = [];
      this.publishMarketInstruments();
      return;
    }

    try {
      const marketDate = formatDate(new Date());
      const [markets, venues] = await Promise.all([
        getMarketsWithAttributes(),
        getVenues(),
      ]);

      if (requestToken !== this.seedRequestToken) {
        return;
      }

      const marketById = new Map(
        (markets || []).map((market) => [String(market.marketId), market]),
      );
      const venueById = new Map(
        (venues || []).map((venue) => [String(venue.venueId), venue]),
      );

      const seedResults = await Promise.allSettled(
        activeMarketControls.map(async (marketControlEntry) => {
          const market = marketById.get(String(marketControlEntry.marketId));
          if (!market) {
            return [];
          }

          const venue = venueById.get(String(market.venueId));
          const [spotTerms, combinations, productState] = await Promise.all([
            getSpotByTermSchedule(
              market.termScheduleId,
              marketDate,
              marketDate,
            ),
            getMarketPriceDriverCombinations(market.marketId),
            getFilteredProductForMarket(
              market.productId,
              market.marketId,
              marketDate,
            ),
          ]);

          const coreCombinationRows = this.buildCoreCombinationRows(
            productState,
            combinations,
          );

          const activeMarketAttributes = (market.marketAttributes || []).filter(
            (marketAttribute) => marketAttribute?.active !== false,
          );

          if (
            !spotTerms?.length ||
            !coreCombinationRows.length ||
            !activeMarketAttributes.length
          ) {
            return [];
          }

          const candidateInputs = new Map();

          for (const spotTerm of spotTerms) {
            for (const marketAttribute of activeMarketAttributes) {
              for (const coreCombinationRow of coreCombinationRows) {
                const qualityAttributeValues =
                  coreCombinationRow?.qualityAttributeValues || [];
                const key = this.buildInstrumentSeedKey({
                  marketId: market.marketId,
                  termId: spotTerm.termId,
                  priceBasisId: marketAttribute.priceBasisId,
                  currencyId: marketAttribute.currencyId,
                  uomId: marketAttribute.uomId,
                  qualityAttributeValues,
                });

                if (candidateInputs.has(key)) {
                  continue;
                }

                candidateInputs.set(key, {
                  key,
                  instrumentInput: {
                    marketId: market.marketId,
                    termId: spotTerm.termId,
                    priceBasisId: marketAttribute.priceBasisId,
                    currencyId: marketAttribute.currencyId,
                    uomId: marketAttribute.uomId,
                    qualityAttributeValues,
                    active: true,
                    eventId: uuid(),
                  },
                  metadata: {
                    marketId: market.marketId,
                    marketName: market.name,
                    termId: spotTerm.termId,
                    termName: spotTerm.name,
                    priceBasisId: marketAttribute.priceBasisId,
                    priceBasisName: marketAttribute.priceBasisName,
                    currencyId: marketAttribute.currencyId,
                    currencyName: marketAttribute.currencyName,
                    currencySymbol: marketAttribute.currencySymbol ?? "$",
                    uomId: marketAttribute.uomId,
                    uomName: marketAttribute.uomName,
                    pricePrecision: marketAttribute.pricePrecision ?? 2,
                    volumePrecision: marketAttribute.volumePrecision ?? 0,
                    qualityAttributeValues,
                    termExpiryDateTime: normalizeExpiryDateTime(
                      spotTerm.expiryDate,
                    ),
                    venueTimeZone: venue?.timeZone ?? null,
                    isExpired: false,
                  },
                });
              }
            }
          }

          const resolvedInstrumentResults = await Promise.allSettled(
            [...candidateInputs.values()].map(async ({ instrumentInput, metadata }) => {
              const response = await getOrAddInstrument(instrumentInput);
              return {
                ...metadata,
                instrumentId: response?.instrumentId,
              };
            }),
          );

          return resolvedInstrumentResults.flatMap((result) => {
            if (result.status !== "fulfilled") {
              console.error(
                "TradingService failed to get or add a configured market instrument:",
                result.reason,
              );
              return [];
            }

            return result.value?.instrumentId ? [result.value] : [];
          });
        }),
      );

      if (requestToken !== this.seedRequestToken) {
        return;
      }

      const nextSeedMarketInstruments = seedResults.flatMap((result) => {
        if (result.status !== "fulfilled") {
          console.error(
            "TradingService failed to seed configured market instruments:",
            result.reason,
          );
          return [];
        }

        return result.value;
      });

      this.seedMarketInstruments = this.mergeInstruments(
        [],
        nextSeedMarketInstruments,
      );
      this.publishMarketInstruments();
    } catch (error) {
      if (requestToken !== this.seedRequestToken) {
        return;
      }

      console.error(
        "TradingService failed to build initial configured market instruments:",
        error,
      );
      this.seedMarketInstruments = [];
      this.publishMarketInstruments();
    }
  }

  getInstrumentId(source) {
    return source?.instrumentId ?? source?.InstrumentId ?? null;
  }

  normalizeInstrument(source) {
    const instrumentId = this.getInstrumentId(source);
    if (!instrumentId) {
      return null;
    }

    return {
      instrumentId,
      marketId: source?.marketId ?? source?.MarketId,
      marketName: source?.marketName ?? source?.MarketName,
      termId: source?.termId ?? source?.TermId,
      termName: source?.termName ?? source?.TermName,
      priceBasisId: source?.priceBasisId ?? source?.PriceBasisId,
      priceBasisName: source?.priceBasisName ?? source?.PriceBasisName,
      currencyId: source?.currencyId ?? source?.CurrencyId,
      currencyName: source?.currencyName ?? source?.CurrencyName,
      currencySymbol: source?.currencySymbol ?? source?.CurrencySymbol ?? "$",
      uomId: source?.uomId ?? source?.UomId,
      uomName: source?.uomName ?? source?.UomName,
      pricePrecision: source?.pricePrecision ?? source?.PricePrecision ?? 2,
      volumePrecision: source?.volumePrecision ?? source?.VolumePrecision ?? 0,
      qualityAttributeValues:
        source?.qualityAttributeValues ?? source?.QualityAttributeValues ?? [],
      termExpiryDateTime:
        source?.termExpiryDateTime ?? source?.TermExpiryDateTime ?? null,
      venueTimeZone: source?.venueTimeZone ?? source?.VenueTimeZone ?? null,
      isExpired: source?.isExpired ?? source?.IsExpired ?? false,
    };
  }

  mergeInstruments(existing = [], incoming = []) {
    const instrumentMap = new Map();

    [...existing, ...incoming]
      .map((instrument) => this.normalizeInstrument(instrument))
      .filter(Boolean)
      .forEach((instrument) => {
        instrumentMap.set(String(instrument.instrumentId), instrument);
      });

    return [...instrumentMap.values()];
  }

  publishMarketInstruments() {
    this.cache.marketInstruments = this.mergeInstruments(
      this.seedMarketInstruments,
      this.engineMarketInstruments,
    );
    this.setters.setMarketInstruments &&
      this.setters.setMarketInstruments(this.cache.marketInstruments);
  }

  mergeMarketInstruments(incoming = []) {
    const nextInstruments = this.mergeInstruments(
      this.engineMarketInstruments,
      incoming,
    );

    const hasChanged =
      nextInstruments.length !== this.engineMarketInstruments.length ||
      nextInstruments.some((instrument, index) => {
        const currentInstrument = this.engineMarketInstruments[index];
        return JSON.stringify(instrument) !== JSON.stringify(currentInstrument);
      });

    if (hasChanged) {
      this.engineMarketInstruments = nextInstruments;
      this.publishMarketInstruments();
    }
  }

  processOrder(prevOrders, order) {
    let orders = [...prevOrders];
    const orderId = order?.orderId ?? order?.OrderId;
    const orderState = order?.orderState ?? order?.OrderState;
    const i = orders.findIndex(
      (candidate) =>
        (candidate?.orderId ?? candidate?.OrderId) === orderId,
    );

    if (i >= 0) {
      if (["Traded", "Withdrawn"].includes(orderState)) {
        orders.splice(i, 1);
      } else {
        orders[i] = order;
      }
    } else if (!["Traded", "Withdrawn"].includes(orderState)) {
      orders.push(order);
    }

    this.cache.marketOrders = orders;
    return orders;
  }

  processTrade(prevTrades, trade) {
    let trades = [...prevTrades];
    const tradeId = trade?.tradeId ?? trade?.TradeId;
    const i = trades.findIndex(
      (candidate) => (candidate?.tradeId ?? candidate?.TradeId) === tradeId,
    );
    if (i >= 0) {
      trades[i] = trade;
    } else {
      trades.push(trade);
    }

    this.cache.marketTrades = trades;
    return trades;
  }

  processOrganizationTrade(prevTrades, trade) {
    let trades = this.processTrade(prevTrades, trade);
    if (trade.OrderDirection === "Buy") {
      toast.info(`Your organization's bid traded: ${trade.buyerOrderId}`);
    } else {
      toast.info(`Your organization's offer traded: ${trade.sellerOrderId}`);
    }
    return trades;
  }

  async invoke(method, data) {
    if (!this.connection) {
      console.error("SignalR connection not established.");
      return;
    }
    try {
      await this.connection.invoke(method, data);
    } catch (error) {
      console.error(`TradingService.${method} failed:`, error);
      const message = error.message?.split("Exception: ")[1] || error.message;
      toast.error(message);
    }
  }

  async submitOrder(order) {
    await this.invoke("SubmitOrder", order);
  }

  async modifyOrder(order) {
    await this.invoke("ModifyOrder", order);
  }

  async withdrawOrder(order) {
    await this.invoke("WithdrawOrder", order);
  }

  async pickOrder(order) {
    await this.invoke("PickOrder", order);
  }

  async disconnect() {
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log("TradingService disconnected.");
      } catch (error) {
        console.error("Error stopping SignalR connection:", error);
      } finally {
        this.connection = null;
        this.connectingPromise = null;
        this.seedRequestToken += 1;
        this.seedMarketInstruments = [];
        this.engineMarketInstruments = [];
        this.cache.marketInstruments = [];
      }
    }
  }
}

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

const tradingService = new TradingService();
export default tradingService;
